import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  TablePagination,
  Box,
} from "@mui/material";
import { Add, RestartAlt } from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_ORIGIN as API_URL } from "../../config/env";
import { resolveMediaUrl } from "../../utils/mediaUrl";
import {
  AdminPageLayout,
  AdminSectionCard,
  TableRowActions,
  DeleteConfirmationDialog,
  AdminAction,
} from "../../components/admin";
import { adminResetButtonSx } from "../../components/admin/adminButtonStyles";

export default function SchemeListPage() {
  const navigate = useNavigate();
  const [schemes, setSchemes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({ state: "", district: "" });
  const [filterKey, setFilterKey] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const hasActiveFilters = !!(filters.state || filters.district);

  const token = localStorage.getItem("rfis");

  const fetchSchemes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/government-schemes`, {
        headers: { rf_token_header123_k: token },
        params: { page: page + 1, limit: rowsPerPage, ...filters },
      });
      setSchemes(res.data.data.schemes);
      setTotal(res.data.data.total);
    } catch (err) {
      toast.error("Failed to fetch schemes");
    }
  };

  useEffect(() => {
    fetchSchemes();
  }, [page, rowsPerPage, filters]);

  const handleResetFilters = () => {
    setFilters({ state: "", district: "" });
    setPage(0);
    setFilterKey((key) => key + 1);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/v1/government-schemes/${deleteTarget.id}`, {
        headers: { rf_token_header123_k: token },
      });
      toast.success("Scheme deleted");
      setDeleteTarget(null);
      fetchSchemes();
    } catch (err) {
      toast.error("Failed to delete scheme");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminPageLayout
      eyebrow="Training Programs"
      title="Government Schemes"
      description=""
      actions={
        <div
          className="admin-toolbar"
          style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}
        >
          <TextField
            key={`state-${filterKey}`}
            size="small"
            label="State"
            value={filters.state}
            sx={{ minWidth: 160 }}
            onChange={(e) => {
              setFilters({ ...filters, state: e.target.value });
              setPage(0);
            }}
          />
          <TextField
            key={`district-${filterKey}`}
            size="small"
            label="District"
            value={filters.district}
            sx={{ minWidth: 160 }}
            onChange={(e) => {
              setFilters({ ...filters, district: e.target.value });
              setPage(0);
            }}
          />
          <AdminAction
            id="reset-filters-btn"
            variant="outline"
            startIcon={<RestartAlt />}
            disabled={!hasActiveFilters}
            onClick={handleResetFilters}
            sx={{ whiteSpace: "nowrap", ...adminResetButtonSx(hasActiveFilters) }}
          >
            Reset
          </AdminAction>
          <AdminAction startIcon={<Add />} onClick={() => navigate("/government-schemes/new")}>
            Add Scheme
          </AdminAction>
        </div>
      }
    >
      <AdminSectionCard
        title="Scheme list"
        description="Manage scheme and training program records from one place."
        className="admin-table-shell"
      >
        <TableContainer component="div">
          <Table sx={{ minWidth: 700 }} aria-label="government schemes table">
            <TableHead>
              <TableRow>
                <TableCell><strong>Image</strong></TableCell>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>State</strong></TableCell>
                <TableCell><strong>District</strong></TableCell>
                <TableCell><strong>Source</strong></TableCell>
                <TableCell><strong>Dates</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schemes.map((scheme) => (
                <TableRow key={scheme.id}>
                  <TableCell>
                    {scheme.imageUrl || scheme.image ? (
                      <Box
                        component="img"
                        src={resolveMediaUrl(scheme.imageUrl || scheme.image)}
                        alt={scheme.title}
                        sx={{
                          width: 56,
                          height: 40,
                          objectFit: "cover",
                          borderRadius: "6px",
                          border: "1px solid #e7e5e4",
                          display: "block",
                          backgroundColor: "#f5f5f4",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 56,
                          height: 40,
                          borderRadius: "6px",
                          border: "1px dashed #d6d3d1",
                          backgroundColor: "#fafaf9",
                          color: "#a8a29e",
                          fontSize: 11,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        —
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{scheme.title}</TableCell>
                  <TableCell>
                    <Chip
                      label={scheme.category}
                      color={scheme.category === "scheme" ? "primary" : "secondary"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{scheme.state || "PAN India"}</TableCell>
                  <TableCell>{scheme.district || "All"}</TableCell>
                  <TableCell>
                    <Chip label={scheme.source} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {scheme.startDate && `${scheme.startDate} - ${scheme.endDate || "Ongoing"}`}
                  </TableCell>
                  <TableCell align="right">
                    <TableRowActions
                      rowId={scheme.id}
                      onEdit={() => navigate(`/government-schemes/${scheme.id}/edit`)}
                      onDelete={() => setDeleteTarget(scheme)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </AdminSectionCard>

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Scheme"
        itemName={deleteTarget?.title}
        loading={deleting}
      />
    </AdminPageLayout>
  );
}
